/** สถานะงานและกติกาการเปลี่ยนสถานะ (pure, ใช้ร่วม client และ server) */
export const FLOW_STATUSES = ["ESTIMATED", "QUOTED", "APPROVED", "PREPARING", "IN_PROGRESS", "INSPECTION", "COMPLETED"] as const;
export const SPECIAL_STATUSES = ["ON_HOLD", "REJECTED", "CANCELLED"] as const;
export const JOB_STATUSES = [...FLOW_STATUSES, ...SPECIAL_STATUSES] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];
export type JobStatus = (typeof JOB_STATUSES)[number];

export const TERMINAL_STATUSES: readonly JobStatus[] = ["COMPLETED", "REJECTED", "CANCELLED"];
/** สถานะที่ต้องใส่เหตุผลเสมอเมื่อเปลี่ยนไป */
export const REASON_REQUIRED: readonly JobStatus[] = ["ON_HOLD", "REJECTED", "CANCELLED"];

const HOLDABLE: readonly JobStatus[] = ["APPROVED", "PREPARING", "IN_PROGRESS"];
const REJECTABLE: readonly JobStatus[] = ["ESTIMATED", "QUOTED"];

export type TransitionKind = "advance" | "back" | "hold" | "resume" | "reject" | "cancel";

export interface TransitionInput {
  from: JobStatus;
  to: JobStatus;
  /** สถานะก่อนพักงาน (จำเป็นเมื่อ from = ON_HOLD) */
  beforeHold?: FlowStatus | null;
  reason?: string;
}

export type TransitionResult = { ok: true; kind: TransitionKind } | { ok: false; error: TransitionError };
export type TransitionError = "terminal" | "same_status" | "not_allowed" | "reason_required" | "back_reason_required";

const isFlow = (s: JobStatus): s is FlowStatus => (FLOW_STATUSES as readonly string[]).includes(s);
const idx = (s: FlowStatus) => FLOW_STATUSES.indexOf(s);

/**
 * กติกา
 * - เดินหน้าได้ครั้งละ 1 ขั้นตามลำดับ
 * - ย้อนได้ 1 ขั้น (เฉพาะสถานะในลำดับหลัก ยกเว้นออกจาก ESTIMATED) ต้องมีเหตุผล
 * - ON_HOLD จาก APPROVED, PREPARING, IN_PROGRESS ต้องมีเหตุผล และกลับได้เฉพาะสถานะก่อนพักงาน
 * - REJECTED จาก ESTIMATED, QUOTED ; CANCELLED จากสถานะที่ยังไม่จบ (รวม ON_HOLD) ทั้งสองต้องมีเหตุผล
 * - COMPLETED, REJECTED, CANCELLED เป็นสถานะสิ้นสุด
 */
export function checkTransition(t: TransitionInput): TransitionResult {
  const { from, to } = t;
  const reason = (t.reason ?? "").trim();
  if (TERMINAL_STATUSES.includes(from)) return { ok: false, error: "terminal" };
  if (from === to) return { ok: false, error: "same_status" };
  if (REASON_REQUIRED.includes(to) && !reason) return { ok: false, error: "reason_required" };

  if (to === "CANCELLED") return { ok: true, kind: "cancel" };
  if (to === "REJECTED") return REJECTABLE.includes(from) ? { ok: true, kind: "reject" } : { ok: false, error: "not_allowed" };
  if (to === "ON_HOLD") return HOLDABLE.includes(from) ? { ok: true, kind: "hold" } : { ok: false, error: "not_allowed" };

  if (from === "ON_HOLD") {
    return t.beforeHold && to === t.beforeHold ? { ok: true, kind: "resume" } : { ok: false, error: "not_allowed" };
  }
  if (isFlow(from) && isFlow(to)) {
    if (idx(to) === idx(from) + 1) return { ok: true, kind: "advance" };
    if (idx(to) === idx(from) - 1) return reason ? { ok: true, kind: "back" } : { ok: false, error: "back_reason_required" };
  }
  return { ok: false, error: "not_allowed" };
}

/** สถานะที่เปลี่ยนไปได้จากสถานะปัจจุบัน (ใช้แสดงปุ่มใน UI) */
export function allowedNext(from: JobStatus, beforeHold?: FlowStatus | null): JobStatus[] {
  return JOB_STATUSES.filter((to) => checkTransition({ from, to, beforeHold, reason: "x" }).ok);
}

/** สถานะถัดไปในลำดับหลัก (ปุ่มหลัก) หรือ null */
export function nextInFlow(from: JobStatus): FlowStatus | null {
  return isFlow(from) && idx(from) < FLOW_STATUSES.length - 1 ? FLOW_STATUSES[idx(from) + 1]! : null;
}

/** % ความคืบหน้าตามสถานะ; IN_PROGRESS ผู้ใช้ override ได้ 0-99 */
export const STATUS_PROGRESS: Record<FlowStatus, number> = {
  ESTIMATED: 0,
  QUOTED: 0,
  APPROVED: 5,
  PREPARING: 15,
  IN_PROGRESS: 50,
  INSPECTION: 90,
  COMPLETED: 100,
};

export function progressFor(status: JobStatus, opts: { beforeHold?: FlowStatus | null; override?: number | null } = {}): number {
  const base: JobStatus = status === "ON_HOLD" ? (opts.beforeHold ?? "ESTIMATED") : status;
  if (!isFlow(base)) return 0; // REJECTED, CANCELLED
  if (base === "IN_PROGRESS" && typeof opts.override === "number") return Math.min(99, Math.max(0, Math.round(opts.override)));
  return STATUS_PROGRESS[base];
}
